import { itemOptionsQuantitiesAtom } from "@/atoms";
import { OptionPrice, OptionQuantityButton, OptionSpecialInstructionChip } from "@/components/item";
import { BottomSheet } from "@/components/modals/BottomSheet";
import {
  formatReducedFraction,
  getPortionFractionBase,
  getPortionTarget,
  isPortionOptionGroup,
  resolveEffectivePortionQuantities,
} from "@/lib/option-group-portions";
import type { Item, NutritionalInfo, OptionGroup } from "@/types";
import { useAtomValue, useSetAtom } from "jotai";

import { Dispatch, MouseEvent, RefObject, SetStateAction, useMemo, useRef, useState } from "react";

import PromotionalRibbon from "@/components/PromotionalRibbon";
import { ImageWithFallback } from "@/components/image/ImageWithFallback";
import { SuitableDietsGrid } from "@/components/item/SuitableDietsGrid";
import { Check } from "@/icons";
import { cn } from "@/lib/cn";
import { Image } from "../image";
import {
  Badge,
  Button,
  Card,
  Collapse,
  Icon,
  RadioIndicator,
  SheetBody,
  SheetDescription,
  Skeleton,
  Text,
  ZoomableImage,
} from "@/components/ui";

type OptionCardProps = {
  item: Item;
  disabled: boolean;
  /**
   * Out of stock: the merchant paused this option (or the item behind it). The
   * card stays on the list, struck through and marked unavailable, but no
   * tap, radio or stepper can select it.
   */
  unavailable?: boolean;
  optionGroup: OptionGroup;
  selectedRadioId: string;
  setSelectedRadioIdAction: Dispatch<SetStateAction<string>>;
};

export default function OptionCard({
  item,
  disabled,
  unavailable = false,
  optionGroup,
  selectedRadioId,
  setSelectedRadioIdAction,
}: OptionCardProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemOptionsQuantities = useAtomValue(itemOptionsQuantitiesAtom);
  const setItemOptionsQuantities = useSetAtom(itemOptionsQuantitiesAtom);
  const option = optionGroup.options.find((op) => op.itemId === item.id);
  const allMaxPermittedOne = optionGroup.options.every((op) => op.maxPermitted === 1);
  const buttonIsRadio = optionGroup.maxPermitted === 1;
  const isPortionGroup = isPortionOptionGroup(optionGroup);
  const portionTarget = getPortionTarget(optionGroup);

  const [isOpen, setIsOpen] = useState(false);

  const maxPriceInGroup = useMemo(() => {
    if (optionGroup.priceMethod !== "MAX" || !option) return 0;
    const quantities = itemOptionsQuantities[optionGroup.id];
    if (!quantities) return 0;

    const selectedOptions = optionGroup.options.filter((opt) => quantities[opt.id] > 0);
    if (selectedOptions.length === 0) return 0;

    return Math.max(...selectedOptions.map((opt) => opt.price.value));
  }, [optionGroup, option, itemOptionsQuantities]);

  if (!option) return null;

  const rawGroupQuantities = itemOptionsQuantities[optionGroup.id] ?? {};
  const effectiveGroupQuantities =
    isPortionGroup && portionTarget
      ? resolveEffectivePortionQuantities(rawGroupQuantities, portionTarget)
      : rawGroupQuantities;
  const portionFractionBase =
    isPortionGroup && portionTarget ? getPortionFractionBase(rawGroupQuantities, portionTarget) : null;

  const effectiveOptionQuantity = effectiveGroupQuantities[option.id] ?? 0;
  const rawOptionQuantity = rawGroupQuantities[option.id] ?? 0;

  const portionPrefix =
    portionFractionBase && rawOptionQuantity > 0 && rawOptionQuantity < portionFractionBase
      ? `${formatReducedFraction(rawOptionQuantity, portionFractionBase)} `
      : "";
  const legacyDividedPrefix =
    !isPortionGroup &&
    optionGroup.minPermitted > 1 &&
    (optionGroup.priceMethod === "MAX" || optionGroup.priceMethod === "AVG")
      ? `1/${optionGroup.minPermitted} `
      : "";
  const itemNamePrefix = portionPrefix || legacyDividedPrefix;

  const isSelected = rawOptionQuantity > 0;

  const optionId: string = option.id;

  function setOptionQuantity(quantity: number): void {
    setItemOptionsQuantities((state) => ({
      ...state,
      [optionGroup.id]: { ...state[optionGroup.id], [optionId]: quantity },
    }));
  }

  function toggleRadioSelection(): void {
    if (selectedRadioId === optionId && optionGroup.minPermitted === 0) {
      setSelectedRadioIdAction("");
      setItemOptionsQuantities((state) => ({ ...state, [optionGroup.id]: {} }));
      return;
    }

    setSelectedRadioIdAction(optionId);
    setItemOptionsQuantities((state) => ({ ...state, [optionGroup.id]: { [optionId]: 1 } }));
  }

  function handleCardClick(): void {
    if (unavailable) return;

    if (buttonIsRadio) {
      toggleRadioSelection();
      return;
    }

    if (rawOptionQuantity > 0) {
      setOptionQuantity(0);
      return;
    }

    if (disabled) return;

    setOptionQuantity(1);
  }

  return (
    <>
      <Card
        tone="primary"
        selected={isSelected}
        dimmed={unavailable ? "opacity" : false}
        padding="sm"
        className={cn(
          "relative w-full",
          unavailable ? "cursor-not-allowed" : "cursor-pointer",
          isSelected ? "overflow-visible" : "overflow-hidden"
        )}
        key={item.id}
        onClick={handleCardClick}
        data-testid={`option-card-${option.id}`}
        data-selected={isSelected}
        data-unavailable={unavailable}
        aria-disabled={unavailable}
      >
        {isSelected && <PopCheck />}
        <div className="flex w-full items-center gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {item.image && (
              <div
                onClick={(event) => event.stopPropagation()}
                className="h-24 w-24 shrink-0 overflow-hidden rounded-control md:h-28 md:w-28"
              >
                <ZoomableImage
                  onZoom={() => setIsOpen(true)}
                  label={`Ver detalhes de ${item.name}`}
                  data-testid={`option-photo-${option.id}`}
                  className="relative h-full w-full"
                >
                  <PromotionalRibbon
                    isPromotional={option.isPromotional}
                    text={option.promotionalBannerText}
                    size="sm"
                  />
                  <Skeleton shape="flat" className="absolute inset-0 h-full w-full" />
                  <Image
                    width={200}
                    height={200}
                    alt={item.name}
                    src={item.image.URL}
                    crc={item.image["CRC-32"]}
                    sizes="(min-width: 768px) 112px, 96px"
                    fit="contain"
                    className="relative h-full w-full"
                  />
                </ZoomableImage>
              </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <Text as="span" weight="bold" clamp={2} className={cn(unavailable && "line-through")}>
                {itemNamePrefix}
                {item.name}
              </Text>
              <Text as="span" size="sm" tone="muted">
                {item.description}
              </Text>
              <OptionPrice
                price={option.price}
                quantity={effectiveOptionQuantity}
                priceMethod={optionGroup.priceMethod}
                maxPrice={maxPriceInGroup}
                isPortionGroup={isPortionGroup}
                portionTarget={portionTarget ?? undefined}
                portionFractionBase={portionFractionBase ?? undefined}
                rawQuantity={rawOptionQuantity}
              />
              {option?.allowsSpecialInstructions !== false && (
                <div className="hidden md:contents" onClick={(e) => e.stopPropagation()}>
                  <Collapse open={isSelected}>
                    <div className="mt-1 flex">
                      <OptionSpecialInstructionChip
                        optionGroupId={optionGroup.id}
                        optionId={option.id}
                        optionName={item.name}
                      />
                    </div>
                  </Collapse>
                </div>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center justify-end">
            {unavailable ? (
              <Text as="span" size="sm">
                <Badge variant="outline" tone="neutral" size="flush">
                  Indisponível
                </Badge>
              </Text>
            ) : buttonIsRadio ? (
              <OptionRadioButton
                buttonRef={buttonRef}
                optionId={option.id}
                selectedRadioId={selectedRadioId}
                onToggle={toggleRadioSelection}
              />
            ) : (
              <OptionQuantityButton
                buttonRef={buttonRef}
                allMaxPermittedOne={allMaxPermittedOne}
                maxPermitted={option.maxPermitted}
                disabled={disabled}
                optionGroupId={optionGroup.id}
                optionId={option.id}
              />
            )}
          </div>
        </div>

        {option?.allowsSpecialInstructions !== false && (
          <div className="w-full md:hidden" onClick={(event: MouseEvent<HTMLDivElement>) => event.stopPropagation()}>
            <Collapse open={isSelected} className="w-full">
              <div className="mt-3 w-full">
                <OptionSpecialInstructionChip
                  optionGroupId={optionGroup.id}
                  optionId={option.id}
                  optionName={item.name}
                  fullWidthOnMobile
                />
              </div>
            </Collapse>
          </div>
        )}
      </Card>
      <OptionModal
        title={item.name}
        description={item.description}
        open={isOpen}
        onOpenChange={setIsOpen}
        image={item.image?.URL}
        imageCrc={item.image?.["CRC-32"]}
        nutritionalInfo={item.nutritionalInfo}
      />
    </>
  );
}

type OptionRadioButtonProps = {
  buttonRef: RefObject<HTMLButtonElement>;
  optionId: string;
  selectedRadioId: string;
  onToggle: () => void;
};

function OptionRadioButton({ buttonRef, optionId, selectedRadioId, onToggle }: OptionRadioButtonProps) {
  function handleClick(event: MouseEvent<HTMLButtonElement>): void {
    event.stopPropagation();
    onToggle();
  }

  return (
    <Button
      ref={buttonRef}
      variant="plain"
      tone="neutral"
      size="icon-md"
      shape="circle"
      onClick={handleClick}
      role="radio"
      aria-checked={selectedRadioId === optionId}
      data-testid={`option-radio-${optionId}`}
    >
      <RadioIndicator checked={selectedRadioId === optionId} size="sm" />
    </Button>
  );
}

function OptionModal({
  description,
  title,
  open,
  onOpenChange,
  image,
  imageCrc,
  nutritionalInfo,
}: {
  description: string;
  title: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  image?: string;
  imageCrc?: string;
  nutritionalInfo?: NutritionalInfo;
}) {
  return (
    <BottomSheet
      modal
      swipeToClose
      open={Boolean(open)}
      onOpenChange={(next) => onOpenChange?.(next)}
      title={title}
      describedByChildren={Boolean(description)}
      onClose={() => onOpenChange?.(false)}
      width="detail"
      headerBordered
      headerAlign="center"
      contentClassName="flex max-h-sheet flex-col"
    >
      <SheetBody className="pb-safe pt-4">
        <div className="flex flex-col gap-4">
          <div className="flex justify-center">
            <ImageWithFallback
              width={500}
              height={500}
              src={image}
              alt="Foto do produto"
              crc={imageCrc}
              priority
              fit="contain"
              className="h-60 w-full rounded-card shadow-card"
            />
          </div>

          {description && <SheetDescription align="center">{description}</SheetDescription>}

          <SuitableDietsGrid suitableDiets={nutritionalInfo?.suitableDiets} />
        </div>
      </SheetBody>
    </BottomSheet>
  );
}

function PopCheck() {
  return (
    <span
      aria-hidden
      className="absolute left-0 top-0 z-raised grid h-7 w-7 -translate-x-1/3 -translate-y-1/3 place-items-center rounded-full bg-primary text-primary-foreground shadow-raised ring-2 ring-card"
    >
      <Icon as={Check} size="sm" weight="bold" />
    </span>
  );
}
